import { create } from 'zustand';
import { TransferSession } from '@share/domain';

// ─── Progress Buffer & RAF-Throttled Flush ───────────────────────────────────
// High-frequency swarm events are buffered in memory and flushed via RAF
// (capped at max 10 renders/sec = 100ms interval) to prevent React UI thrashing.
const progressBuffer = new Map<string, Partial<TransferSession>>();
let rafId: any = null;
let timerId: any = null;
let lastFlushTime = 0;
const MIN_FLUSH_INTERVAL_MS = 100; // Max 10 renders/sec

const getScheduleFn = (): ((cb: () => void) => any) => {
  if (typeof requestAnimationFrame === 'function') {
    return requestAnimationFrame;
  }
  if (typeof globalThis !== 'undefined' && typeof (globalThis as any).requestAnimationFrame === 'function') {
    return (globalThis as any).requestAnimationFrame;
  }
  return (cb: () => void) => setTimeout(cb, 16);
};

const getCancelFn = (): ((id: any) => void) => {
  if (typeof cancelAnimationFrame === 'function') {
    return cancelAnimationFrame;
  }
  if (typeof globalThis !== 'undefined' && typeof (globalThis as any).cancelAnimationFrame === 'function') {
    return (globalThis as any).cancelAnimationFrame;
  }
  return (id: any) => clearTimeout(id);
};

function flushProgressBuffer(set: (fn: (state: TransferState) => Partial<TransferState>) => void): void {
  const cancel = getCancelFn();
  if (rafId !== null) {
    cancel(rafId);
    rafId = null;
  }
  if (timerId !== null) {
    clearTimeout(timerId);
    timerId = null;
  }

  if (progressBuffer.size === 0) return;

  const snapshot = new Map(progressBuffer);
  progressBuffer.clear();
  lastFlushTime = Date.now();

  set((state) => {
    let hasChanges = false;
    const updatedSessions = { ...state.sessions };

    for (const [id, updates] of snapshot.entries()) {
      const existing = updatedSessions[id];
      if (existing) {
        updatedSessions[id] = { ...existing, ...updates };
        hasChanges = true;
      }
    }

    return hasChanges ? { sessions: updatedSessions } : state;
  });
}

function scheduleProgressFlush(set: (fn: (state: TransferState) => Partial<TransferState>) => void): void {
  if (rafId !== null || timerId !== null) {
    return; // Flush already queued
  }

  const now = Date.now();
  const elapsed = now - lastFlushTime;
  const remaining = MIN_FLUSH_INTERVAL_MS - elapsed;
  const scheduleRaf = getScheduleFn();

  if (remaining <= 0) {
    rafId = scheduleRaf(() => {
      rafId = null;
      flushProgressBuffer(set);
    });
  } else {
    timerId = setTimeout(() => {
      timerId = null;
      rafId = scheduleRaf(() => {
        rafId = null;
        flushProgressBuffer(set);
      });
    }, remaining);
  }
}

interface TransferState {
  sessions: Record<string, TransferSession>;
  activeSessionId: string | null;
  addSession: (session: TransferSession) => void;
  updateSession: (id: string, updates: Partial<TransferSession>) => void;
  updateProgress: (id: string, updates: Partial<TransferSession>) => void;
  flushProgress: () => void;
  removeSession: (id: string) => void;
  setActiveSessionId: (id: string | null) => void;
  setSessions: (sessions: Record<string, TransferSession>) => void;
}

export const useTransferStore = create<TransferState>((set) => ({
  sessions: {},
  activeSessionId: null,
  addSession: (session) =>
    set((state) => ({
      sessions: { ...state.sessions, [session.id]: session },
    })),
  updateSession: (id, updates) => {
    set((state) => {
      const existing = state.sessions[id];
      if (!existing) return {};

      // If there is any buffered progress for this session, merge it inside atomic callback
      const buffered = progressBuffer.get(id);
      if (buffered) {
        progressBuffer.delete(id);
      }
      const mergedUpdates = buffered ? { ...buffered, ...updates } : updates;

      return {
        sessions: {
          ...state.sessions,
          [id]: { ...existing, ...mergedUpdates },
        },
      };
    });
  },
  updateProgress: (id, updates) => {
    const existing = progressBuffer.get(id);
    progressBuffer.set(id, existing ? { ...existing, ...updates } : updates);
    scheduleProgressFlush(set);
  },
  flushProgress: () => {
    flushProgressBuffer(set);
  },
  removeSession: (id) => {
    progressBuffer.delete(id);
    set((state) => {
      const { [id]: _, ...remaining } = state.sessions;
      return { sessions: remaining };
    });
  },
  setActiveSessionId: (id) => set({ activeSessionId: id }),
  setSessions: (sessions) => set({ sessions }),
}));

// ─── Selectors ────────────────────────────────────────────────────────────────
// Use these in components instead of accessing .sessions directly.
// This keeps filtering logic in one place and prevents stale-closure bugs.

/** All sessions as an ordered array (most-recently-added last). */
export const selectAllSessions = (s: TransferState): TransferSession[] =>
  Object.values(s.sessions);

/** A single session by id — returns undefined if not found. */
export const selectSession =
  (id: string) =>
  (s: TransferState): TransferSession | undefined =>
    s.sessions[id];

/** Sessions filtered to send direction only. */
export const selectSendSessions = (s: TransferState): TransferSession[] =>
  Object.values(s.sessions).filter((sess) => sess.direction === 'send');

/** Sessions filtered to receive direction only. */
export const selectReceiveSessions = (s: TransferState): TransferSession[] =>
  Object.values(s.sessions).filter((sess) => sess.direction === 'receive');

/** Find an existing session matching a given v1 infoHash (case-insensitive). */
export const selectSessionByInfoHash =
  (infoHash: string) =>
  (s: TransferState): TransferSession | undefined => {
    const target = infoHash.toLowerCase();
    return Object.values(s.sessions).find(
      (sess) => sess.torrentIdentity?.v1?.toLowerCase() === target
    );
  };

